/**
 * Observable state container used by flow execution and the React renderer.
 *
 * Mutations produce a fresh top-level snapshot (immutable update), which
 * matters for React/useSyncExternalStore consumers that rely on referential
 * change to detect updates.
 */

type Listener = () => void;

export class StateStore {
  private state: Record<string, unknown>;
  private listeners = new Set<Listener>();

  constructor(initial: Record<string, unknown> = {}) {
    this.state = { ...initial };
  }

  get(scope: string): unknown {
    return this.state[scope];
  }

  getAll(): Record<string, unknown> {
    return this.state;
  }

  set(scope: string, value: unknown): void {
    if (Object.is(this.state[scope], value)) return;
    this.state = { ...this.state, [scope]: value };
    this.notify();
  }

  setRoot(next: Record<string, unknown>): void {
    this.state = { ...next };
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Read a dotted or bracketed renderer binding path.
   *
   * Examples:
   *   @session.userId  -> session slot, userId property
   *   state.session.x  -> state root alias, session slot, x property
   *   @state.language  -> state root alias, language slot
   *   items[0].name    -> items slot, first entry, name property
   */
  readPath(pathStr: string): unknown {
    const cleaned = String(pathStr ?? "").trim().replace(/^@/, "");
    if (!cleaned) return undefined;

    const normalized = cleaned.replace(
      /\[(\d+|"[^"]+"|'[^']+')\]/g,
      (_, key: string) => {
        const segment = String(key).replace(/^["']|["']$/g, "");
        return `.${segment}`;
      },
    );

    const parts = normalized.split(".").filter(Boolean);
    if (parts.length === 0) return undefined;

    const isStateAlias = parts[0] === "state";
    const stateParts = isStateAlias ? parts.slice(1) : parts;
    if (isStateAlias && stateParts.length === 0) return this.state;

    const head = stateParts[0];
    if (!head) return undefined;

    let current: unknown = this.state[head];
    for (const segment of stateParts.slice(1)) {
      if (
        current === null ||
        current === undefined ||
        typeof current !== "object"
      ) {
        return undefined;
      }
      current = (current as Record<string, unknown>)[segment];
    }

    return current;
  }

  private notify(): void {
    for (const listener of Array.from(this.listeners)) {
      try {
        listener();
      } catch {
        // Ignore listener failures so one bad subscriber doesn't break updates.
      }
    }
  }
}